import { makeAutoObservable } from 'mobx'
import moment from 'moment'

import { isValidSessionId, PARTNER_CONTEXT_STORAGE_KEY } from 'utils/partnerSession.utils'
import { LocalStorageService } from 'utils/local-storage.utils'
import { ApiClientV2Default } from 'utils/api-client/api-client.utils'
import { JSONResponse, JSONAPIError } from 'utils/api-client/types'

import { createPartnerApi, PartnerApi, ResolveWebSdkSessionResponse } from '../api/partner.api'

/**
 * PartnerStore - Manages Web SDK partner session state
 * Shared across shop and cabinet apps
 *
 * Handles:
 * - Session resolution from UUID
 * - Partner context storage (MobX + localStorage)
 * - Redirect back to partner platform
 */
export class PartnerStore {
  storeName = 'PartnerStore'

  context: ResolveWebSdkSessionResponse | null = null
  loading = false
  error: string | null = null

  private childApi: PartnerApi

  constructor() {
    // Initialize API client with error handling at request level
    const client = new ApiClientV2Default()
    this.childApi = createPartnerApi(client)

    makeAutoObservable(this, { hasValidContext: false })

    // Restore from localStorage on init (client-side only)
    this.restoreFromStorage()
  }

  /**
   * Check if we have a valid (non-expired) partner context
   */
  get hasValidContext(): boolean {
    if (!this.context) return false
    return moment.utc(this.context.expiresAt).isAfter(moment.utc())
  }

  /**
   * Get the partner's return URL
   */
  get returnUrl(): string | null | undefined {
    return this.context?.returnUrl
  }

  /**
   * Get the partner name for display
   */
  get partnerName(): string | null | undefined {
    return this.context?.partnerName
  }

  /**
   * Get the current flow type
   */
  get flowType(): string | null | undefined {
    return this.context?.flow
  }

  toggleLoading = (value: boolean) => {
    this.loading = value
  }

  updateContext = (context: ResolveWebSdkSessionResponse) => {
    this.context = context
    this.error = null
    this.persistToStorage()
  }

  setError = (error: string | null) => {
    this.error = error
  }

  /**
   * Resolve a shop Web SDK session UUID to partner context.
   */
  resolveShopSession = (sessionId: string): Promise<JSONResponse<ResolveWebSdkSessionResponse> | JSONAPIError> =>
    this.resolve(sessionId, () => this.childApi.resolveShopWebSdkSession({ session_id: sessionId }))

  /**
   * Resolve a sales-team Web SDK session, scoped to the currently selected team.
   * salesTeamId is required; a NaN/invalid id (e.g. no team resolved yet) fails cleanly
   * instead of requesting /sales_teams/NaN/web-sdk-session.
   */
  resolveSalesTeamSession = (
    sessionId: string,
    salesTeamId: number
  ): Promise<JSONResponse<ResolveWebSdkSessionResponse> | JSONAPIError> => {
    if (!Number.isInteger(salesTeamId)) {
      return Promise.resolve(this.fail('No sales team selected'))
    }
    return this.resolve(sessionId, () =>
      this.childApi.resolveSalesTeamWebSdkSession({ session_id: sessionId, sales_team_id: salesTeamId })
    )
  }

  /**
   * Shared resolve plumbing: validate the session id, run the flavor-specific API call,
   * and fold the result into store state.
   */
  private resolve = async (
    sessionId: string,
    call: () => Promise<JSONResponse<ResolveWebSdkSessionResponse> | JSONAPIError>
  ): Promise<JSONResponse<ResolveWebSdkSessionResponse> | JSONAPIError> => {
    if (!isValidSessionId(sessionId)) {
      return this.fail('Invalid session ID format')
    }

    this.toggleLoading(true)
    this.setError(null)

    try {
      const response = await call()

      if (response?.responseType === 'success') {
        // The data is already flattened and in camelCase by withSerializedJSON() interceptor
        // It contains id + all attributes merged together
        const contextData = response.data
        if (contextData && typeof contextData === 'object' && 'partnerId' in contextData) {
          this.updateContext(contextData)
          this.toggleLoading(false)
          return response
        }
        // If we have data but it doesn't match expected structure, treat as error
        this.setError('Invalid response format')
      } else if (response?.responseType === 'error') {
        // Error response - extract error message from normalized error structure
        const errors = response.errors || []
        const errorMessage = errors?.[0]?.detail || errors?.[0]?.title || 'Failed to resolve session'
        this.setError(errorMessage)
      } else {
        // Unexpected format
        this.setError('Failed to resolve session')
      }

      this.toggleLoading(false)
      return response
    } catch (e) {
      // Network errors or other exceptions
      const errorMessage = e instanceof Error ? e.message : 'Failed to resolve session'
      return this.fail(errorMessage)
    }
  }

  /**
   * Set the store into an error state and return the standard JSON:API error shape.
   */
  private fail = (message: string): JSONAPIError => {
    this.toggleLoading(false)
    this.setError(message)
    return {
      responseType: 'error',
      errors: [{ title: message, detail: message }],
    } as JSONAPIError
  }

  /**
   * Clear partner context (after redirect or on error)
   */
  clearContext = () => {
    this.context = null
    this.error = null
    this.removeFromStorage()
  }

  /**
   * Redirect to partner return URL with status and reference_id
   * @param status - 'success' for completed purchase, 'cancel' for abandoned/cancelled
   * @param context - context to redirect with; defaults to the live store context. The deal
   *   detail page clears the store on render (to avoid leaking partner state into the normal
   *   flow) yet still needs to redirect from a snapshot, so it passes its captured context.
   */
  redirectToPartner(
    status: 'success' | 'cancel' = 'success',
    context: ResolveWebSdkSessionResponse | null = this.context
  ): void {
    if (!context?.returnUrl) return

    // Build URL with query params
    const url = new URL(context.returnUrl)
    url.searchParams.set('status', status)
    url.searchParams.set('reference_id', context.referenceId || '')

    const finalUrl = url.toString()
    this.clearContext()
    window.location.href = finalUrl
  }

  /**
   * Persist context to localStorage
   */
  private persistToStorage(): void {
    if (!this.context) return

    LocalStorageService.setItem(PARTNER_CONTEXT_STORAGE_KEY, this.context)
  }

  /**
   * Restore context from localStorage
   */
  private restoreFromStorage(): void {
    const stored = LocalStorageService.getItem(PARTNER_CONTEXT_STORAGE_KEY)
    if (stored) {
      const parsed = stored as ResolveWebSdkSessionResponse
      if (moment.utc(parsed.expiresAt).isAfter(moment.utc())) {
        this.context = parsed
      } else {
        this.removeFromStorage()
      }
    }
  }

  /**
   * Remove context from localStorage
   */
  private removeFromStorage(): void {
    LocalStorageService.removeItem(PARTNER_CONTEXT_STORAGE_KEY)
  }
}
